import { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { Users, Car, Calendar, DollarSign } from 'lucide-react';

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      const response = await api.get('/dashboard/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Failed to load stats:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-shizah-blue"></div>
      </div>
    );
  }

  const statCards = [
    {
      title: 'Total Drivers',
      value: stats?.total_drivers || 0,
      icon: Users,
      color: 'bg-blue-500',
      testId: 'stat-drivers',
    },
    {
      title: 'Total Vehicles',
      value: stats?.total_vehicles || 0,
      icon: Car,
      color: 'bg-green-500',
      testId: 'stat-vehicles',
    },
    {
      title: 'Active Assignments',
      value: stats?.active_assignments || 0,
      icon: Calendar,
      color: 'bg-purple-500',
      testId: 'stat-assignments',
    },
    {
      title: 'Monthly Salary',
      value: `₹${stats?.monthly_salary_total?.toLocaleString() || 0}`,
      icon: DollarSign,
      color: 'bg-orange-500',
      testId: 'stat-salary',
    },
  ];

  return (
    <div data-testid="dashboard-page">
      <div className="mb-8">
        <h1 className="text-4xl font-black font-heading text-gray-900 mb-2">Dashboard</h1>
        <p className="text-gray-600">Overview of your fleet management system</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {statCards.map((card) => {
          const Icon = card.icon;
          return (
            <div
              key={card.title}
              data-testid={card.testId}
              className="bg-white rounded-lg border border-gray-200 p-6 hover:shadow-lg transition-all duration-200"
            >
              <div className="flex items-center justify-between mb-4">
                <div className={`${card.color} p-3 rounded-md`}>
                  <Icon className="text-white" size={24} />
                </div>
              </div>
              <h3 className="text-sm font-medium text-gray-600 mb-1">{card.title}</h3>
              <p className="text-3xl font-bold font-heading text-gray-900">{card.value}</p>
            </div>
          );
        })}
      </div>

      <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h2 className="text-xl font-bold font-heading text-gray-900 mb-4">Quick Actions</h2>
          <div className="space-y-3">
            <a
              href="/drivers/new"
              data-testid="quick-add-driver"
              className="block px-4 py-3 bg-shizah-blue text-white rounded-md hover:bg-blue-700 transition-colors duration-200 font-medium"
            >
              Add New Driver
            </a>
            <a
              href="/vehicles/new"
              data-testid="quick-add-vehicle"
              className="block px-4 py-3 bg-shizah-green text-white rounded-md hover:bg-green-600 transition-colors duration-200 font-medium"
            >
              Add New Vehicle
            </a>
            <a
              href="/assignments"
              data-testid="quick-assignments"
              className="block px-4 py-3 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors duration-200 font-medium"
            >
              Manage Assignments
            </a>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h2 className="text-xl font-bold font-heading text-gray-900 mb-4">System Info</h2>
          <div className="space-y-3 text-gray-600">
            <div className="flex justify-between">
              <span>Active Drivers:</span>
              <span className="font-semibold text-gray-900">{stats?.total_drivers || 0}</span>
            </div>
            <div className="flex justify-between">
              <span>Available Vehicles:</span>
              <span className="font-semibold text-gray-900">
                {(stats?.total_vehicles || 0) - (stats?.active_assignments || 0)}
              </span>
            </div>
            <div className="flex justify-between">
              <span>Assigned Vehicles:</span>
              <span className="font-semibold text-gray-900">{stats?.active_assignments || 0}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}